/**
 * IonQuotaCard (WS 2.2 Task B) — the conversion moment for a chat-quota
 * exhaustion (429 `quota_exceeded`), replacing the generic red error note.
 *
 * Same conversion lever as the deep-dive `DailyLimitModal`
 * (components/shared/DailyLimitModal.jsx): value-framed, ONE primary CTA,
 * no price shown, no dead-end. Reuses that modal's "Talk to our team"
 * destination + `lead_source` attribution so the sales funnel counts both
 * triggers (deep-dive limit vs. Ion question limit) consistently.
 *
 * Body copy is NOT re-localised here — `error.message` (set by
 * `useChatStream.markError` via `quotaMessageForScope`) already carries the
 * scope-aware, reset-aware text. The `ion.access.quota_exceeded.body` key
 * is kept only as a last-resort fallback for a malformed/empty message.
 */
import { useLocation } from 'wouter-preact'
import { trackEvent } from '../../modules/dataService.js'
import { t } from '../../i18n/index.js'

/**
 * @param {object} props
 * @param {{code: string, message: string, recoverable: boolean}} props.error
 */
export function IonQuotaCard({ error }) {
  const [, setLocation] = useLocation()

  function onPrimaryCta() {
    // Same attribution the daily-limit modal uses, so the /contact inquiry
    // submit tags this conversion event identically regardless of which
    // limit (deep-dive views vs. Ion questions) triggered it.
    trackEvent('Ion Quota Upgrade Clicked', { lead_source: 'ion_quota_card' })
    try {
      sessionStorage.setItem('demo_lead_source', 'ion_quota_card')
    } catch (e) {
      /* sessionStorage unavailable (private mode) — attribution best-effort */
    }
    // Lightweight "Talk to our team" contact page (demo users have no price).
    setLocation('/contact')
  }

  const body = error?.message || t('ion.access.quota_exceeded.body')

  return (
    <div class="ion-quota-card" role="alert">
      <p class="ion-quota-card__title">{t('ion.access.quota_exceeded.title')}</p>
      <p class="ion-quota-card__body">{body}</p>
      <button class="ion-quota-card__cta" onClick={onPrimaryCta} type="button">
        {t('ion.access.quota_exceeded.cta')}
      </button>
    </div>
  )
}
